import { useState } from 'react';
import { WorkspaceGrid, useWorkspaceList } from './WorkspaceGrid';
import { WorkspaceEntityDetail } from './WorkspaceEntityDetail';
import { MergeDrawer } from './MergeDrawer';
import { WorkspaceCreateDialog } from './WorkspaceCreateDialog';
import { TypeBadge } from './CharacterMeta';

const LinkedBadge = ({ x, originKey }) => x.sync_group
  ? <span className="text-[9px] px-1.5 py-px rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/25">synced</span>
  : x[originKey] ? <span className="text-[9px] px-1.5 py-px rounded-full bg-white/[0.05] text-[#8A8A93] border border-white/10">copied</span> : null;

function useWorkspaceActions(kind, list) {
  const { projects, setItems, reload } = list;
  const [merge, setMerge] = useState(null); // null | { groups: null } (scan) | { groups: [{items}] } (manual)
  const [creating, setCreating] = useState(false);
  const props = {
    onFindDuplicates: () => setMerge({ groups: null }),
    onMergeSelected: (items) => setMerge({ groups: [{ items, reasons: {}, already_merged: false }] }),
    onCreate: () => setCreating(true),
  };
  const overlays = (
    <>
      {merge && <MergeDrawer kind={kind} groups={merge.groups} onClose={() => setMerge(null)} onMerged={() => reload()} />}
      {creating && <WorkspaceCreateDialog kind={kind} projects={projects} onClose={() => setCreating(false)} onCreated={(x) => setItems(prev => [...prev, x])} />}
    </>
  );
  return { props, overlays };
}

export function WorkspaceCharactersTab() {
  const list = useWorkspaceList('/workspace/characters', 'characters');
  const { items, projects, loading, setItems } = list;
  const [type, setType] = useState('all');
  const [open, setOpen] = useState(null);
  const { props: actionProps, overlays } = useWorkspaceActions('character', list);
  return (
    <>
      <WorkspaceGrid kind="character" items={items} projects={projects} loading={loading} imageOf={c => c.image_storage_path} {...actionProps}
        filterFn={type === 'all' ? null : (c) => (c.character_type || 'main') === type}
        extraFilters={(
          <select data-testid="workspace-type-filter" value={type} onChange={e => setType(e.target.value)} className="ff-input text-xs py-2">
            <option value="all">All types</option><option value="main">Main</option><option value="background">Background</option><option value="extra">Extra</option>
          </select>
        )}
        renderMeta={(c) => (
          <div className="mt-1.5 flex flex-wrap gap-1 items-center">
            <TypeBadge type={c.character_type} />
            {(c.tags || []).slice(0, 2).map(t => <span key={t} className="text-[9px] px-1.5 py-px rounded-full bg-[#A78BFA]/10 text-[#C4B5FD] border border-[#A78BFA]/20">{t}</span>)}
            <LinkedBadge x={c} originKey="origin_character_id" />
          </div>
        )}
        onOpen={setOpen} />
      {open && <WorkspaceEntityDetail kind="character" item={open} onClose={() => setOpen(null)}
        onUpdated={(u) => setItems(prev => prev.map(x => x.id === u.id ? { ...x, ...u } : x))} />}
      {overlays}
    </>
  );
}

export function WorkspaceLocationsTab() {
  const list = useWorkspaceList('/workspace/locations', 'locations');
  const { items, projects, loading, setItems } = list;
  const [open, setOpen] = useState(null);
  const { props: actionProps, overlays } = useWorkspaceActions('location', list);
  return (
    <>
      <WorkspaceGrid kind="location" items={items} projects={projects} loading={loading} imageOf={l => l.image_path} {...actionProps}
        renderMeta={(l) => <p className="text-[10px] text-[#55555C] truncate mt-1 flex items-center gap-1"><span className="truncate">{l.description || '—'}</span><LinkedBadge x={l} originKey="origin_location_id" /></p>}
        onOpen={setOpen} />
      {open && <WorkspaceEntityDetail kind="location" item={open} onClose={() => setOpen(null)}
        onUpdated={(u) => setItems(prev => prev.map(x => x.id === u.id ? { ...x, ...u } : x))} />}
      {overlays}
    </>
  );
}
